import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Provider } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { GlobalStyles } from './styles/GlobalStyles';
import { theme } from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import { store } from './store';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import StaffPage from './pages/StaffPage';
import ManageGuardPage from './pages/manage_guard/view/ManageGuardPage';
import AttendancePage from './pages/attendance/view/AttendancePage';
import FactoryClientPage from './pages/manage_clients/factory_clients/view/FactoryClientPage';
import IndividualClientPage from './pages/manage_clients/individual_clients/view/IndividualClientPage';
import BankClientPage from './pages/manage_clients/bank_clients/view/BankClientPage';
import DeploymentPage from './pages/deployment/view/DeploymentPage';
import IndividualDeploymentPage from './pages/deployment/view/IndividualDeploymentPage';
import FactoryDeploymentPage from './pages/deployment/view/FactoryDeploymentPage';
import BankDeploymentPage from './pages/deployment/view/BankDeploymentPage';
import TransactionPage from './pages/finance/view/TransactionPage';
import InvoiceRevenuePage from './pages/finance/view/InvoiceRevenuePage';
import ExpensePage from './pages/finance/view/ExpensePage';
import InvoiceManagementPage from './pages/finance/view/InvoiceManagementPage';
import AccountReceivablePage from './pages/finance/view/AccountReceivablePage';
import ProfitLossPage from './pages/finance/view/ProfitLossPage';
import PettyCashBookPage from './pages/finance/view/PettyCashBookPage';
import ExpenseManagementPage from './pages/finance/view/ExpenseManagementPage';
import WeaponInventoryPage from './pages/inventory/weapons/view/WeaponInventoryPage';
import ProfilePage from './pages/profile/ProfilePage';
import ChangePasswordPage from './pages/profile/ChangePasswordPage';
import CompanyComplainsPage from './pages/complains/view/CompanyComplainsPage';
import CompanyComplainDetailPage from './pages/complains/view/CompanyComplainDetailPage';
import CompanyNotificationsPage from './pages/notifications/CompanyNotificationsPage';
import PublicBranchComplainsPage from './pages/complains/view/PublicBranchComplainsPage';
import PublicComplainPage from './pages/complains/view/PublicComplainPage';

const App: React.FC = () => {
    return (
        <Provider store={store}>
            <CustomThemeProvider>
                <ThemeProvider theme={theme}>
                    <GlobalStyles />
                    <AuthProvider>
                        <Router>
                            <Routes>
                                {/* Public routes */}
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/complain" element={<PublicComplainPage />} />
                                <Route
                                    path="/bankClient/:companyId/banks/:bankId/branches/:branchId"
                                    element={<PublicBranchComplainsPage />}
                                />

                                {/* Protected routes */}
                                <Route
                                    path="/dashboard"
                                    element={
                                        <ProtectedRoute>
                                            <DashboardPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/staff"
                                    element={
                                        <ProtectedRoute>
                                            <StaffPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/manage-guards"
                                    element={
                                        <ProtectedRoute>
                                            <ManageGuardPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/attendance"
                                    element={
                                        <ProtectedRoute>
                                            <AttendancePage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/manage-clients/factory"
                                    element={
                                        <ProtectedRoute>
                                            <FactoryClientPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/manage-clients/individual"
                                    element={
                                        <ProtectedRoute>
                                            <IndividualClientPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/manage-clients/bank"
                                    element={
                                        <ProtectedRoute>
                                            <BankClientPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/deployment"
                                    element={
                                        <ProtectedRoute>
                                            <DeploymentPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/deployment/individual"
                                    element={
                                        <ProtectedRoute>
                                            <IndividualDeploymentPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/deployment/factory"
                                    element={
                                        <ProtectedRoute>
                                            <FactoryDeploymentPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/deployment/bank"
                                    element={
                                        <ProtectedRoute>
                                            <BankDeploymentPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/finance/transaction"
                                    element={
                                        <ProtectedRoute>
                                            <TransactionPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/finance/invoice-revenue"
                                    element={
                                        <ProtectedRoute>
                                            <InvoiceRevenuePage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/finance/expense"
                                    element={
                                        <ProtectedRoute>
                                            <ExpensePage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/finance/invoice-management"
                                    element={
                                        <ProtectedRoute>
                                            <InvoiceManagementPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/finance/account-receivable"
                                    element={
                                        <ProtectedRoute>
                                            <AccountReceivablePage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/finance/profit-loss"
                                    element={
                                        <ProtectedRoute>
                                            <ProfitLossPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/finance/petty-cash"
                                    element={
                                        <ProtectedRoute>
                                            <PettyCashBookPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/finance/expense-management"
                                    element={
                                        <ProtectedRoute>
                                            <ExpenseManagementPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/inventory/weapons"
                                    element={
                                        <ProtectedRoute>
                                            <WeaponInventoryPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/profile"
                                    element={
                                        <ProtectedRoute>
                                            <ProfilePage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/change-password"
                                    element={
                                        <ProtectedRoute>
                                            <ChangePasswordPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/companies/:companyId/complains/:complainId"
                                    element={
                                        <ProtectedRoute>
                                            <CompanyComplainDetailPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/companies/:companyId/complains"
                                    element={
                                        <ProtectedRoute>
                                            <CompanyComplainsPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/companies/:companyId/notifications"
                                    element={
                                        <ProtectedRoute>
                                            <CompanyNotificationsPage />
                                        </ProtectedRoute>
                                    }
                                />

                                {/* Default redirect */}
                                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                                {/* Catch all route */}
                                <Route path="*" element={<Navigate to="/dashboard" replace />} />
                            </Routes>
                        </Router>
                        <Toaster
                            position="top-right"
                            toastOptions={{
                                duration: 4000,
                                style: {
                                    background: theme.colors.white,
                                    color: theme.colors.light.text.primary,
                                    border: `1px solid ${theme.colors.light.border.medium}`,
                                    borderRadius: theme.borderRadius.md,
                                    boxShadow: theme.shadows.lg,
                                },
                                success: {
                                    iconTheme: {
                                        primary: theme.colors.accent.success,
                                        secondary: theme.colors.white,
                                    },
                                },
                                error: {
                                    iconTheme: {
                                        primary: theme.colors.accent.danger,
                                        secondary: theme.colors.white,
                                    },
                                },
                            }}
                        />
                    </AuthProvider>
                </ThemeProvider>
            </CustomThemeProvider>
        </Provider>
    );
};

export default App;
